import { 
  TreePine, Scale, UserCheck, GraduationCap, Zap, Leaf, Heart, Target, Globe, 
  MapPin, Plane, Laptop, DollarSign, Briefcase, Car, Award, Landmark, Settings, 
  Shield, Tag, Building, Wrench, FileText
} from 'lucide-react';

// Map each policy tag to a specific Lucide React icon
export const getPolicyTagIcon = (tag) => {
  const tagIcons = {
    'US Economy & Jobs': Briefcase,
    'Healthcare': Heart,
    'Education': GraduationCap,
    'Immigration & Border Security': MapPin,
    'Environment & Climate': Leaf,
    'Crime & Justice': Scale,
    'Foreign Policy & Defense': Globe,
    'Taxes & Government Spending': DollarSign,
    'Energy': Zap,
    'Disability': UserCheck,
    'Family & Social Issues': Heart,
    'Agriculture Policy': TreePine,
    'Technology & Privacy': Laptop,
    'International Trade': Plane,
    'Firearm Policy': Target,
    'Other': Tag,
    'Transportation & Infrastructure': Car,
    'Ceremonial & Honorary': Award,
    'Local Government & Municipal Affairs': Building,
    'Administrative & Regulatory': Settings,
    'Technical & Procedural Updates': FileText
  };
  
  return tagIcons[tag] || Tag;
};

// Map each policy tag to an emoji
export const getPolicyTagEmoji = (tag) => {
  const tagEmojis = {
    'US Economy & Jobs': '💼',
    'Healthcare': '🏥',
    'Education': '🎓',
    'Immigration & Border Security': '📍',
    'Environment & Climate': '🌱',
    'Crime & Justice': '⚖️',
    'Foreign Policy & Defense': '🌍',
    'Taxes & Government Spending': '💰',
    'Energy': '⚡',
    'Disability': '♿',
    'Family & Social Issues': '❤️',
    'Agriculture Policy': '🌾',
    'Technology & Privacy': '💻',
    'International Trade': '✈️',
    'Firearm Policy': '🎯',
    'Other': '🏷️',
    'Transportation & Infrastructure': '🚗',
    'Ceremonial & Honorary': '🏆',
    'Local Government & Municipal Affairs': '🏛️',
    'Administrative & Regulatory': '⚙️',
    'Technical & Procedural Updates': '📋'
  };
  
  return tagEmojis[tag] || '🏷️';
};

// Get consistent colors for policy tags
export const getPolicyTagColor = (tag, isDarkMode) => {
  const colors = [
    isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
    isDarkMode ? 'bg-pink-900/50 text-pink-300' : 'bg-pink-100 text-pink-800',
    isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-800',
    isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800',
    isDarkMode ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-800'
  ];
  
  // Simple hash to get consistent color for same tag
  const hash = tag.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return colors[Math.abs(hash) % colors.length];
};

// Cache for policy tags fetched from Supabase
let policyTagsCache = null;
let policyTagsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get all available policy tags from Supabase bill_tags table
// This is now the source of truth - fetches from API which queries Supabase
export const getAllPolicyTags = async () => {
  // Return cached tags if available and fresh
  if (policyTagsCache && policyTagsCacheTime && (Date.now() - policyTagsCacheTime) < CACHE_DURATION) {
    return policyTagsCache;
  }

  try {
    // Fetch from API endpoint which queries Supabase bill_tags table
    const response = await fetch('/api/policy-tags');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // Extract tag names (handle both object format with 'name' property and string format)
    const tagNames = data
      .map((tag) => (typeof tag === 'string' ? tag : tag.name))
      .filter(Boolean)
      .sort();
    
    // Update cache
    policyTagsCache = tagNames;
    policyTagsCacheTime = Date.now();
    
    return tagNames;
  } catch (error) {
    console.error('❌ Error fetching policy tags from API:', error);
    
    // Fallback to hardcoded list if API fails
    const fallbackTags = [
      'US Economy & Jobs',
      'Healthcare',
      'Education',
      'Immigration & Border Security',
      'Environment & Climate',
      'Crime & Justice',
      'Foreign Policy & Defense',
      'Taxes & Government Spending',
      'Energy',
      'Disability',
      'Family & Social Issues',
      'Agriculture Policy',
      'Technology & Privacy',
      'International Trade',
      'Firearm Policy',
      'Other',
      'Transportation & Infrastructure',
      'Ceremonial & Honorary',
      'Local Government & Municipal Affairs',
      'Administrative & Regulatory',
      'Technical & Procedural Updates'
    ];
    
    // Cache fallback for a shorter duration
    policyTagsCache = fallbackTags;
    policyTagsCacheTime = Date.now();
    
    return fallbackTags;
  }
};

// Synchronous version for backward compatibility (uses cache or fallback)
// Note: This should be replaced with async getAllPolicyTags() in components
export const getAllPolicyTagsSync = () => {
  if (policyTagsCache) {
    return policyTagsCache;
  }
  
  // Return fallback if cache is empty
  return [
    'US Economy & Jobs',
    'Healthcare',
    'Education',
    'Immigration & Border Security',
    'Environment & Climate',
    'Crime & Justice',
    'Foreign Policy & Defense',
    'Taxes & Government Spending',
    'Energy',
    'Disability',
    'Family & Social Issues',
    'Agriculture Policy',
    'Technology & Privacy',
    'International Trade',
    'Firearm Policy',
    'Other',
    'Transportation & Infrastructure',
    'Ceremonial & Honorary',
    'Local Government & Municipal Affairs',
    'Administrative & Regulatory',
    'Technical & Procedural Updates'
  ];
}; 